/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  Typography,
  Card,
  message,
  Badge,
  Input,
  Button,
  Space,
  DatePicker,
  Row,
  Col,
  Spin,
  Modal,
  Select,
  Tag,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import Heading from "../../components/Heading";
import urls from "../../api/urls";
import API from "../../api";
import constants from "../../constants/constants";
import "./style.scss";

const { CP_ADMIN, CP_OPERATOR } = constants.ROLES;

const { Text } = Typography;
const { Option } = Select;

const DeliveryInvoiceReport = () => {
  const userinfo = JSON.parse(localStorage.getItem("userinfo")) || {};
  const userRole = userinfo?.roles?.[0];
  const isCPUser = [CP_ADMIN, CP_OPERATOR].includes(userRole);
  const cpRefId = isCPUser ? userinfo?.spId : null;
  const cpSpName = isCPUser
    ? (JSON.parse(localStorage.getItem("userId")) || {})?.premises?.premiseName
    : null;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [apiColumns, setApiColumns] = useState([]);
  const [tableConfig, setTableConfig] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
  });

  const [columnFilters, setColumnFilters] = useState({});
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [exportDate, setExportDate] = useState(moment());
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [areCompaniesLoading, setAreCompaniesLoading] = useState(false);
  const apiCallInProgress = useRef(false);

  const fetchReportData = async (
    page = 1,
    pageSize = 20,
    currentFilters = columnFilters,
  ) => {
    if (apiCallInProgress.current) return;

    setLoading(true);
    apiCallInProgress.current = true;

    try {
      const queryParams = {
        page: page,
        limit: pageSize,
      };

      if (isCPUser && cpRefId) {
        queryParams.refId = cpRefId;
      }

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && String(value).trim() !== "") {
          queryParams[key] = String(value).trim();
        }
      });

      const queryString = new URLSearchParams(queryParams).toString();
      const response = await API.Api(
        urls.DELIVERY_INVOICE_REPORT + "?" + queryString,
      ).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (response?.response?.data?.status === "success") {
        const resData = response.response.data;
        setData(resData.data || []);

        if (resData.pagination) {
          setPagination({
            current: page,
            pageSize: pageSize,
            total: resData.pagination.totalCount || 0,
            showSizeChanger: true,
            pageSizeOptions: resData.pagination.pageSizeOptions?.map(
              String,
            ) || ["20", "50", "100", "200"],
          });
        }

        if (resData.tableConfig) {
          setTableConfig(resData.tableConfig);
        }

        if (resData.columns) {
          setApiColumns(resData.columns);
        }
      } else {
        message.error(
          response?.response?.data?.message || "Failed to fetch report data",
        );
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      message.error("Error fetching report data");
    } finally {
      setLoading(false);
      apiCallInProgress.current = false;
    }
  };

  const fetchCompanies = async () => {
    setAreCompaniesLoading(true);
    try {
      const response = await API.Api(
        `${urls.GET_HUB_WISE_COMPANY_LIST}`,
      ).getWithToken();
      if (response?.status && response?.response?.data?.data) {
        setCompanies(response.response.data.data);
      } else if (response?.data?.data) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setAreCompaniesLoading(false);
    }
  };

  const getColumnSearchProps = (col) => {
    const dataIndex = col.key;
    const title = col.title;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search ${title}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              handleSearch(dataIndex, selectedKeys[0]);
              confirm();
            }}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(dataIndex, selectedKeys[0]);
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                handleSearch(dataIndex, "");
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      filteredValue: columnFilters[dataIndex]
        ? [columnFilters[dataIndex]]
        : null,
    };
  };

  const handleSearch = (field, value) => {
    const newFilters = { ...columnFilters, [field]: value };
    setColumnFilters(newFilters);
    fetchReportData(1, pagination.pageSize, newFilters);
  };

  const columns = useMemo(() => {
    return apiColumns.map((col) => {
      const formattedCol = {
        title: col.title,
        dataIndex: col.key,
        key: col.key,
        width: col.width || 150,
        sorter: col.sortable,
        ellipsis: true,
      };

      if (col.filter && col.filter.type === "search") {
        Object.assign(formattedCol, getColumnSearchProps(col));
      }

      if (col.render) {
        if (col.render.type === "datetime") {
          formattedCol.render = (text) =>
            text
              ? moment(text).format(col.render.format || "DD-MM-YYYY HH:mm:ss")
              : "-";
        } else if (col.key === "shipmentStatus") {
          formattedCol.render = (status) => (
            <Badge
              status={
                status?.toLowerCase() === "delivered" ? "success" : "processing"
              }
              text={status}
            />
          );
        } else if (col.key === "awbNumber") {
          formattedCol.render = (text) => <Text strong>{text}</Text>;
        }
      }

      return formattedCol;
    });
  }, [apiColumns, columnFilters]);

  const handleTableChange = (pag, filters) => {
    const formattedFilters = { ...columnFilters };
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        [formattedFilters[key]] = filters[key];
      } else {
        delete formattedFilters[key];
      }
    });

    setColumnFilters(formattedFilters);
    fetchReportData(pag.current, pag.pageSize, formattedFilters);
  };

  const showExportModal = () => {
    setIsExportModalVisible(true);
    if (companies.length === 0) {
      fetchCompanies();
    }
  };

  const handleExportConfirm = async () => {
    setLoading(true);
    setIsExportModalVisible(false);
    try {
      const month = exportDate.month() + 1;
      const year = exportDate.year();

      const queryParams = {
        export: true,
        month: month,
        year: year,
      };

      if (selectedCompanies && selectedCompanies.length > 0) {
        queryParams.deliveryCompanyIds = selectedCompanies.join(",");
      }

      const queryString = new URLSearchParams(queryParams).toString();

      const userData =
        JSON.parse(localStorage.getItem("userinfo")) ||
        JSON.parse(sessionStorage.getItem("userinfo"));

      const axiosResponse = await axios.get(
        urls.DELIVERY_INVOICE_REPORT + "?" + queryString,
        {
          responseType: "arraybuffer",
          headers: {
            Authorization: "Bearer " + userData?.accessToken,
            kid: "1",
            authtype: "jwt",
          },
        },
      );

      if (axiosResponse?.status === 200 && axiosResponse?.data) {
        const blob = new Blob([axiosResponse.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Delivery_Invoice_Report_${year}_${month}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success("Export successful");
      } else {
        message.error("Failed to export data");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("Error exporting data");
    } finally {
      setLoading(false);
      setSelectedCompanies([]);
    }
  };

  useEffect(() => {
    if (isCPUser && cpSpName && companies.length > 0) {
      const matched = companies.find(
        (c) => c.SpName?.toLowerCase() === cpSpName.toLowerCase(),
      );
      if (matched) {
        setSelectedCompanies([matched.CompanyID]);
      }
    }
  }, [companies]);

  useEffect(() => {
    fetchReportData();
  }, []);

  return (
    <div className="delivery-invoice-report-container">
      <Spin spinning={loading}>
        <div className="report-header">
          <Heading title={tableConfig?.title || "Delivery Invoice Report"} />
        </div>

        <Card className="report-card">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            <Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={showExportModal}
              >
                Export
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() =>
                  fetchReportData(pagination.current, pagination.pageSize)
                }
              >
                Refresh
              </Button>
              <Button
                icon={<ClearOutlined />}
                onClick={() => {
                  setColumnFilters({});
                  fetchReportData(1, pagination.pageSize, {});
                }}
              >
                Clear Filters
              </Button>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              ...pagination,
              showTotal: (total) => `Total ${total} items`,
            }}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ x: "max-content" }}
            bordered
            size="middle"
          />
        </Card>
      </Spin>

      <Modal
        title="Export Delivery Invoice Report"
        open={isExportModalVisible}
        onOk={handleExportConfirm}
        onCancel={() => setIsExportModalVisible(false)}
        okText="Export"
        width={600}
      >
        <div style={{ padding: "10px 0" }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Select Month & Year:
              </Text>
              <DatePicker
                picker="month"
                value={exportDate}
                onChange={(date) => setExportDate(date)}
                disabledDate={(current) =>
                  current && current > moment().endOf("month")
                }
                allowClear={false}
                style={{ width: "100%" }}
              />
            </Col>
            <Col span={24}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Select Companies:
              </Text>
              <Select
                mode="multiple"
                showSearch
                style={{ width: "100%" }}
                placeholder="Search and select companies"
                optionFilterProp="children"
                onChange={(value) => setSelectedCompanies(value)}
                value={selectedCompanies}
                loading={areCompaniesLoading}
                disabled={isCPUser}
                filterOption={(input, option) =>
                  (option.label || "")
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {companies.map((c) => (
                  <Option
                    key={c.CompanyID}
                    value={c.CompanyID}
                    label={`${c.CompanyName} ${c.SeriesCode} ${c.CPName ? ` - ${c.CPName} (${c.CPID})` : ""
                    }`}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{c.CompanyName}</span>
                      {c.SeriesCode && (
                        <Tag
                          color="blue"
                          style={{ marginLeft: 8, marginRight: 0 }}
                        >
                          SC: {c.SeriesCode}
                        </Tag>
                      )}
                    </div>
                    {c.CPName && (
                      <div style={{ fontSize: "11px", color: "gray" }}>
                        {c.CPName} ({c.CPID})
                      </div>
                    )}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default DeliveryInvoiceReport;
